import React from 'react'
import { Tooltip, useTranslation } from '@paintscout/ui'
import type { TooltipProps } from '@paintscout/ui'
import type { Payment } from 'paintscout'
import { getFailureReason, getPaymentFailureText, getWisetackFailedLabel } from '@paintscout/util/builder'
import PaymentStatusBadge from './PaymentStatusBadge'
import type { PaymentStatusBadgeProps } from './PaymentStatusBadge'

export interface PaymentStatusBadgeWithTooltipProps {
  status: Payment['status']
  amount: number
  type: string
  balanceDue: number
  achLabel: string
  failedEvents?: string[]
  failureReason?: Payment['failureReason']
  /** Customers see customer-safe, translated failure text; staff see the specific reason. */
  inPresentation?: boolean
  /**
   * Where the tooltip opens relative to the badge. Defaults to MUI's `bottom`. Set this to open
   * toward the page interior when the badge sits near a viewport edge (e.g. `left` for a
   * right-aligned badge), so the tooltip isn't clipped by the edge of the page.
   */
  placement?: TooltipProps['placement']
  /** Passed through to the badge — e.g. the estimateNext mobile payment row resizes it (ENG-2071). */
  badgeClasses?: PaymentStatusBadgeProps['classes']
}

/**
 * A PaymentStatusBadge wrapped with a contextual tooltip explaining the payment's status:
 * - pending requests that exceed the balance due
 * - how long processing payments (ACH/EFT vs. card) take to resolve, or that a Wisetack loan is en route
 * - why a failed payment failed (customer-safe text in presentation). A failed Wisetack payout instead
 *   reads "Cancelled"/"Declined" on the badge, with no tooltip.
 *
 * Falls back to a bare badge when there's nothing useful to explain.
 */
export default function PaymentStatusBadgeWithTooltip({
  status,
  amount,
  type,
  balanceDue,
  achLabel,
  failedEvents,
  failureReason,
  inPresentation,
  placement,
  badgeClasses
}: PaymentStatusBadgeWithTooltipProps) {
  const { __ } = useTranslation()
  // A cancelled/declined Wisetack payout says so on the badge itself, so it needs no tooltip.
  const wisetackLabel = getWisetackFailedLabel({ type, status, failedEvents })
  const reason =
    status === 'failed' && !wisetackLabel ? getFailureReason({ payment: { failureReason, failedEvents } }) : null
  // Same line as the activity and ViewPaymentDialog
  const failedTooltip =
    status === 'failed' && !wisetackLabel
      ? __(getPaymentFailureText(reason, inPresentation ? 'customer' : 'contractor'))
      : null

  const tooltipContent =
    status === 'pending' && amount > balanceDue
      ? 'Requested amount exceeds balance due. Your customer will not be able to see this request.'
      : status === 'processing'
        ? type?.toLowerCase() === 'wisetack'
          ? 'The customer has released the loan funds. Wisetack is sending them to your bank account.'
          : ['ach', 'eft'].includes(type?.toLowerCase())
            ? `${achLabel} payments usually take up to 5 business days to resolve.`
            : 'This payment should resolve within a few hours.'
        : failedTooltip

  const badge = <PaymentStatusBadge status={status} label={wisetackLabel ?? undefined} classes={badgeClasses} />

  if (tooltipContent) {
    return (
      <Tooltip hideIcon content={tooltipContent} placement={placement}>
        {badge}
      </Tooltip>
    )
  }
  return badge
}
